import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../../hooks/useUser'
import { timeDifference } from '../../util/helpers'

// Compact version of the trial / no-subscription banner, designed to sit
// at the bottom of the v2 nav (just above the user select). Same trigger
// conditions as the top-bar version in TopCallouts.tsx, just styled to fit
// a narrow column.
//
// Returns null when there's no subscription warning to show.
export const NavSubscriptionWarning: React.FC = () => {
    const navigate = useNavigate()
    const { user, activeSubscription } = useUser()

    if (!user) return null

    // Case 1: no subscription at all (post-trial, never subscribed). The most
    // urgent state — gets the red treatment.
    if (!activeSubscription) {
        return (
            <Shell tone="urgent">
                <div className="font-semibold text-mjRed-600 leading-tight">
                    No active subscription
                </div>
                <div className="text-mjDark-800 text-[12px] mt-0.5 leading-snug">
                    Please subscribe to continue using Merch Jar.
                </div>
                <SubscribeButton onClick={() => navigate('/billing')} tone="urgent" />
            </Shell>
        )
    }

    // Case 2: active subscription in trial. Show days remaining.
    if (activeSubscription.isTrialing()) {
        const start = Date.now()
        const end = new Date((activeSubscription.trial_end ?? 0) * 1000).getTime()
        const { daysDifference, hoursDifference } = timeDifference(end, start)
        const days = hoursDifference >= 12 ? daysDifference + 1 : daysDifference
        const expired = days <= 0

        if (expired) {
            return (
                <Shell tone="urgent">
                    <div className="font-semibold text-mjRed-600 leading-tight">
                        No active subscription
                    </div>
                    <div className="text-mjDark-800 text-[12px] mt-0.5 leading-snug">
                        Please subscribe to continue using Merch Jar.
                    </div>
                    <SubscribeButton onClick={() => navigate('/billing')} tone="urgent" />
                </Shell>
            )
        }

        const headline = days === 1 ? '1 day left on trial!' : `${days} days left on trial!`

        return (
            <Shell tone="info">
                <div className="font-semibold text-mjDark-1400 leading-tight">{headline}</div>
                <div className="text-mjDark-800 text-[12px] mt-0.5 leading-snug">
                    Subscribe to avoid interruption.
                </div>
                <SubscribeButton onClick={() => navigate('/billing')} tone="info" />
            </Shell>
        )
    }

    return null
}

// Card-ish container giving the warning its own visual space within the nav.
const Shell: React.FC<{ tone: 'urgent' | 'info'; children: React.ReactNode }> = ({
    tone,
    children,
}) => {
    const bg = tone === 'urgent' ? 'bg-mjRed-100' : 'bg-white'
    const border = tone === 'urgent' ? 'border-mjRed-200' : 'border-mjGray-600'
    return (
        <div className={`mx-2 mb-2 px-3 py-2 rounded-md border text-[13px] ${bg} ${border}`}>
            {children}
        </div>
    )
}

const SubscribeButton: React.FC<{ onClick: () => void; tone: 'urgent' | 'info' }> = ({
    onClick,
    tone,
}) => {
    const base =
        'mt-2 w-full inline-flex items-center justify-center px-2 py-1 rounded text-[12px] font-medium transition-colors duration-75 active:scale-[0.98] transition-transform'
    const palette =
        tone === 'urgent'
            ? 'bg-mjRed-500 text-white hover:bg-mjRed-600'
            : 'bg-mjBlue-500 text-white hover:bg-mjBlue-600'
    return (
        <button type="button" onClick={onClick} className={`${base} ${palette}`}>
            Subscribe now
        </button>
    )
}
